/**
 * The dashboard's assessment column (PRD-0075 R-D2, ADR-0095): the open hypotheses with their
 * latest verdict, confidence and the trend from the assessment before, and the latest assessment
 * note — the newest judgment in words, from a hypothesis or an investigation.
 */
import { ArrowDownRight, ArrowRight, ArrowUpRight, Lightbulb, NotebookPen } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { VerdictBadge } from '@/components/analytic-hypotheses/HypothesisCard'
import type { HypothesisTrend } from '@/services/api/case-dashboard'
import { formatDate } from '@/utils/date'
import { DashboardPanel, panelRows } from './DashboardPanel'
import type {
  DashboardHypothesisView,
  DashboardNoteView,
  DashboardPanelsView,
} from './dashboard-view'

const COPY = 'caseDashboard.panels.assessment'

/** Towards supported wears the supported colour, towards refuted the refuted one (the strip's). */
const TREND_MARKS: Record<HypothesisTrend, { icon: typeof ArrowRight; tone: string }> = {
  up: { icon: ArrowUpRight, tone: 'text-success' },
  down: { icon: ArrowDownRight, tone: 'text-destructive' },
  steady: { icon: ArrowRight, tone: 'text-muted-foreground' },
}

function TrendMark({ hypothesis }: { hypothesis: DashboardHypothesisView }) {
  const { t } = useTranslation()
  if (!hypothesis.trend) return null
  const { icon: Icon, tone } = TREND_MARKS[hypothesis.trend]
  const previous = hypothesis.previousVerdict
    ? t(`analyticHypotheses.verdict.${hypothesis.previousVerdict}`)
    : ''
  const label = t(`${COPY}.trend.${hypothesis.trend}`, { previous })
  return (
    <span role="img" aria-label={label} title={label} className={`shrink-0 ${tone}`}>
      <Icon aria-hidden className="size-4" />
    </span>
  )
}

function HypothesisRow({ hypothesis }: { hypothesis: DashboardHypothesisView }) {
  const { t, i18n } = useTranslation()
  return (
    <li className="flex flex-col gap-1.5 rounded-md border border-border/60 px-3 py-2">
      <div className="flex min-w-0 items-center gap-2">
        <Link
          to={hypothesis.href}
          className="min-w-0 flex-1 truncate text-sm font-medium text-foreground underline-offset-2 hover:underline"
        >
          {hypothesis.title}
        </Link>
        <TrendMark hypothesis={hypothesis} />
      </div>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <VerdictBadge point={hypothesis.point} />
        {hypothesis.assessedAt ? (
          <span className="text-xs text-muted-foreground">
            {t(`${COPY}.assessedOn`, { date: formatDate(hypothesis.assessedAt, i18n.language) })}
          </span>
        ) : null}
      </div>
    </li>
  )
}

function NoteBody({ note }: { note: DashboardNoteView }) {
  const { t, i18n } = useTranslation()
  const { about } = note
  return (
    <figure className="flex flex-col gap-2">
      <blockquote className="line-clamp-6 whitespace-pre-line text-sm leading-relaxed text-foreground">
        {note.text}
      </blockquote>
      <figcaption className="flex flex-col gap-1 text-xs text-muted-foreground">
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
          {t(`${COPY}.noteBy`, {
            author: note.authorName,
            date: formatDate(note.writtenAt, i18n.language),
          })}
          {note.point ? <VerdictBadge point={note.point} /> : null}
        </span>
        {about ? (
          <span className="min-w-0 truncate">
            {t(`${COPY}.about.${about.kind}`)}{' '}
            <Link to={about.href} className="text-foreground underline-offset-2 hover:underline">
              {about.title}
            </Link>
          </span>
        ) : null}
      </figcaption>
    </figure>
  )
}

export function AssessmentPanel({ panels }: { panels: DashboardPanelsView }) {
  const { t } = useTranslation()
  const hypotheses = panelRows(panels.hypotheses, t(`${COPY}.empty`))
  const note = panels.latestNote
  return (
    <div className="flex flex-col gap-4">
      <DashboardPanel
        heading={{ id: 'dashboard-hypotheses', icon: Lightbulb, title: t(`${COPY}.title`) }}
        link={{ href: panels.links.hypotheses, label: t(`${COPY}.all`) }}
        rows={hypotheses.rows}
      >
        <ul className="flex flex-col gap-2">
          {hypotheses.shown.map(hypothesis => (
            <HypothesisRow key={hypothesis.id} hypothesis={hypothesis} />
          ))}
        </ul>
      </DashboardPanel>
      <DashboardPanel
        heading={{ id: 'dashboard-latest-note', icon: NotebookPen, title: t(`${COPY}.latestNote`) }}
        link={null}
        rows={{ total: note ? 1 : 0, shown: note ? 1 : 0, empty: t(`${COPY}.noNote`) }}
      >
        {note ? <NoteBody note={note} /> : null}
      </DashboardPanel>
    </div>
  )
}
